import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    Alert,
    CircularProgress,
    Sheet,
    Chip
} from '@mui/joy';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BlockIcon from '@mui/icons-material/Block';
import axiosConfig from '../util/axiosConfig';
import UserContext from '../context/UserContext';
import ActiveLiveQuizBanner from '../quiz/ActiveLiveQuizBanner';
import UnreadNotificationsPreview from './UnreadNotificationsPreview';
import CampusOnlineConfirmPreview from './CampusOnlineConfirmPreview';
import ScheduledAssessmentPreview from './ScheduledAssessmentPreview';

const StudentDetails = () => {
    const { user } = useContext(UserContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [studentData, setStudentData] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);

    useEffect(() => {
        fetchStudentDetails();
    }, []);

    const fetchStudentDetails = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/student-details');
            setStudentData(response.data);
            setPhotoUrl(response.data?.photoUrl || null);
        } catch (err) {
            console.error('Error fetching student details:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load student details');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading student details...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!studentData) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="warning" variant="soft">
                    No student data available
                </Alert>
            </Box>
        );
    }

    // Helper function to get color based on percentage
    const getPercentageColor = (percentage) => {
        if (percentage < 40) {
            return 'danger'; // Red
        } else if (percentage >= 40 && percentage < 60) {
            return 'warning'; // Orange
        } else if (percentage >= 60 && percentage < 75) {
            return null; // Will use custom yellow-green color
        } else {
            return 'success'; // Green
        }
    };

    // Helper function to get custom styles for yellow-green (60-75 range)
    const getCustomColorStyles = (percentage) => {
        if (percentage >= 60 && percentage < 75) {
            return {
                backgroundColor: '#d4e157', // Light yellow-green
                color: '#33691e', // Dark green text
            };
        }
        return {};
    };

    return (
        <Box sx={{ p: 2, maxWidth: '1200px', mx: 'auto' }}>
            {/* Basic Information Card */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <PersonIcon sx={{ color: '#eb7a24' }} />
                        <Typography level="title-lg" sx={{ color: '#eb7a24' }}>
                            Student Information
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
                            <Box sx={{ minWidth: '150px' }}>
                                <Typography level="body-sm" sx={{ color: '#606162', mb: 0.5 }}>
                                    Student ID
                                </Typography>
                                <Typography level="body-lg" sx={{ fontWeight: 'bold' }}>
                                    {studentData.studentId}
                                </Typography>
                            </Box>
                            <Box sx={{ minWidth: '150px' }}>
                                <Typography level="body-sm" sx={{ color: '#606162', mb: 0.5 }}>
                                    Name
                                </Typography>
                                <Typography level="body-lg" sx={{ fontWeight: 'bold' }}>
                                    {studentData.name}
                                </Typography>
                            </Box>
                            {studentData.batchCode && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '150px' }}>
                                    <SchoolIcon sx={{ color: '#eb7a24', fontSize: '20px' }} />
                                    <Box>
                                        <Typography level="body-sm" sx={{ color: '#606162', mb: 0.5 }}>
                                            Batch
                                        </Typography>
                                        <Typography level="body-lg" sx={{ fontWeight: 'bold' }}>
                                            {studentData.batchCode}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                        <Box
                            sx={{
                                width: 120,
                                height: 150,
                                flexShrink: 0,
                                border: '2px solid #eb7a24',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                bgcolor: '#f19b30',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {photoUrl ? (
                                <Box
                                    component="img"
                                    src={photoUrl}
                                    alt={studentData.name || 'Student Photo'}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                                />
                            ) : (
                                <Typography sx={{ color: 'white', fontSize: '2rem', fontWeight: 'bold' }}>
                                    {studentData.name ? studentData.name.charAt(0).toUpperCase() : 'S'}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <ScheduledAssessmentPreview />

            <CampusOnlineConfirmPreview />

            <UnreadNotificationsPreview />

            <ActiveLiveQuizBanner />

            {/* Blacklist Alert */}
            {studentData.isBlacklisted && (
                <Alert
                    color="danger"
                    variant="soft"
                    startDecorator={<BlockIcon />}
                    sx={{ mb: 3 }}
                >
                    <Typography level="title-md" sx={{ color: 'inherit', fontWeight: 'bold' }}>
                        You have been Blacklisted from Placement
                    </Typography>
                </Alert>
            )}

            {/* Test Marks Section */}
            {studentData.testMarks && studentData.testMarks.length > 0 && (
                <Card variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <AssessmentIcon sx={{ color: '#eb7a24' }} />
                            <Typography level="title-lg" sx={{ color: '#eb7a24' }}>
                                Marks Percentage
                            </Typography>
                        </Box>
                        <Sheet
                            variant="outlined"
                            sx={{
                                borderRadius: 'sm',
                                overflow: 'auto',
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <Table
                                aria-label="marks table"
                                stickyHeader
                                hoverRow
                                sx={{
                                    '& thead th': {
                                        backgroundColor: '#f5f5f5',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1
                                    },
                                    '& tbody td': {
                                        textAlign: 'center'
                                    },
                                    '& tbody tr:nth-of-type(even)': {
                                        backgroundColor: '#fafafa'
                                    },
                                    '& tbody tr:hover': {
                                        backgroundColor: '#fff5e6'
                                    }
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th style={{ width: '60%', padding: '12px' }}>Module</th>
                                        <th style={{ width: '40%', padding: '12px' }}>Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {studentData.testMarks.map((mark, index) => (
                                        <tr key={index}>
                                            <td style={{ padding: '12px', fontWeight: 'medium' }}>
                                                {mark.module}
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                                    <Chip
                                                        variant="solid"
                                                        color={getPercentageColor(mark.percentage)}
                                                        size="md"
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            minWidth: '70px',
                                                            ...getCustomColorStyles(mark.percentage)
                                                        }}
                                                    >
                                                        {mark.percentage}%
                                                    </Chip>
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr
                                        style={{
                                            backgroundColor: '#fff5e6',
                                            fontWeight: 'bold',
                                            borderTop: '2px solid #eb7a24'
                                        }}
                                    >
                                        <td style={{ padding: '12px' }}>Total Average</td>
                                        <td style={{ padding: '12px' }}>
                                            <Chip
                                                variant="solid"
                                                color={getPercentageColor(studentData.totalAverage)}
                                                size="lg"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    minWidth: '80px',
                                                    fontSize: '1rem',
                                                    ...getCustomColorStyles(studentData.totalAverage)
                                                }}
                                            >
                                                {studentData.totalAverage}%
                                            </Chip>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Sheet>
                    </CardContent>
                </Card>
            )}

            {/* No Test Marks Message */}
            {(!studentData.testMarks || studentData.testMarks.length === 0) && (
                <Card variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AssessmentIcon sx={{ color: '#606162' }} />
                            <Typography level="body-lg" sx={{ color: '#606162' }}>
                                No test marks available
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default StudentDetails;

